import Logger from '@/logger'
import SchemeServicingManager from '@/scheme-servicing-manager'
import Store from '@/store'

import Project from '@/models/project'

import { MUTATIONS } from '@/store/mutations'
import { SERVICE_OFFERINGS, FEATURES } from '@/schemes/scheme-config'

/**
 * @type {string}
 */
const LOGGING_CATEGORY = 'Progress Manager'

/**
 * Returns true if the scheme has the shopping basket feature and
 * the provided service available and the service has been selected.
 * @param {Scheme} scheme
 * @param {string} service
 * @returns {boolean}
 */
const fulfillsServiceRequirements = (scheme, service) =>
    scheme.hasFeature(FEATURES.SHOPPING_BASKET)
        && scheme.hasService(service)
        && Project.ensure(Store.state.improvements.currentProject).hasService(service)

const ProgressManager = {
    /**
     * Marks the supplied route as having been visited and stores in state.
     * @param {string} routeName The name of the route to mark as visited.
     */
    markVisitedRoute(routeName) {
        Store.commit(MUTATIONS.markVisitedRoute, routeName)
    },

    /**
     * Remove the supplied route from the visited list and store in state.
     * @param {string} routeName The name of the route to remove.
     */
    clearVisitedRoute(routeName) {
        Store.commit(MUTATIONS.clearVisitedRoute, routeName)
    },

    /**
     * Clears all visited routes in state.
     */
    clearVisitedRoutes() {
        Store.commit(MUTATIONS.clearAllVisitedRoutes)
    },

    /**
     * Returns true if the supplied route has been visited.
     * @param {string} routeName
     * @returns {boolean}
     */
    hasVisitedRoute(routeName) {
        return Store.state.visitedRoutes.includes(routeName)
    },

    /**
     * Returns true if the supplied routes have all been visited.
     * @param {string[]} routeNames
     * @returns {boolean}
     */
    hasVisitedAllRoutes(routeNames) {
        return routeNames.every(rn => Store.state.visitedRoutes.includes(rn))
    },

    /**
     * Returns true if the user has reached the summary screen in the flow.
     * @returns {boolean}
     */
    hasVisitedSummary() {
        return this.hasVisitedRoute('PlanSummaryReview')
    },

    /**
     * Returns the next route for the supplied route name.
     * @param {string} currentRoute Name of the current route.
     * @param {?Object} query Optional query object to use with the route.
     * @returns {Object}
     */
    getNext(currentRoute, query = null) {
        const scheme = SchemeServicingManager.getActiveScheme()
        let newRoute

        Logger.log(`getNext for ${ currentRoute }`, LOGGING_CATEGORY)

        switch (currentRoute) {
            case 'Welcome':
                newRoute = query != null
                    ? { name: 'Search', query }
                    : { name: 'Search' }
                break
            case 'Search':
                newRoute = query != null
                    ? { name: 'AddressChooser', query }
                    : { name: 'AddressChooser' }
                break
            case 'AddressChooser':
                newRoute = query != null
                    ? { name: 'MyHome', query }
                    : { name: 'MyHome' }
                break
            case 'MyHome':
            case 'PlanChooser':
            // This jumps a step forward and the route guard pushes it back if
            // the required data hasn't been set
                newRoute = { name: 'PlanBuilder' }
                break
            case 'MyHomeEdit':
                newRoute = query != null
                    ? { name: 'MyHome', query }
                    : { name: 'MyHome' }
                break
            case 'PlanBuilder':
                newRoute = this.hasVisitedSummary()
                    ? { name: 'PlanSummaryReview' }
                    : { name: 'PlanNotesForm' }
                break
            case 'PlanNotesForm':
                newRoute = scheme.userOnlyTool || this.hasVisitedSummary()
                    ? { name: 'PlanSummaryReview' }
                    : { name: 'PlanServices' }
                break
            case 'PlanServices':
            // if scheme has the home-energy-plan service available, and
            // the user has chosen the home-energy-plan
                newRoute = fulfillsServiceRequirements(scheme, SERVICE_OFFERINGS.homeEnergyPlan)
                    ? { name: 'HomeEnergyPlan' }
                    : fulfillsServiceRequirements(scheme, SERVICE_OFFERINGS.financeOptions) ? { name: 'FinancialServices' } : { name: 'PlanSummaryReview' }
                break
            case 'HomeEnergyPlan':
            // if scheme has the financial-services service available, and
            // the user has chosen the financial-services
                newRoute = fulfillsServiceRequirements(scheme, SERVICE_OFFERINGS.financeOptions)
                    ? { name: 'FinancialServices' }
                    : { name: 'BasketSummary' }
                break
            case 'FinancialServices':
                newRoute = { name: 'BasketSummary' }
                break
            case 'BasketSummary':
            // if all services pages have been visited this goes to PlanSummaryReview
            // if not it goes to the next non-visited available service page
                newRoute = fulfillsServiceRequirements(scheme, SERVICE_OFFERINGS.homeEnergyPlan) && !this.hasVisitedRoute('HomeEnergyPlan')
                    ? { name: 'HomeEnergyPlan' }
                    : fulfillsServiceRequirements(scheme, SERVICE_OFFERINGS.financeOptions) && !this.hasVisitedRoute('FinancialServices')
                        ? { name: 'FinancialServices' } : { name: 'PlanSummaryReview' }
                break
            case 'PlanSummaryReview':
                newRoute = { name: 'GoAhead' }
                break
            default:
            // not sure if this is a good default case, but the linter insists
                newRoute = { name: 'Welcome' }
        }

        Logger.logObject(newRoute, 'Returning new route', LOGGING_CATEGORY)

        return newRoute
    },

    /**
     * Returns the previous route for the supplied route name.
     * @param {string} currentRoute Name of the current route.
     * @returns {Object}
     */
    getPrevious(currentRoute) {
        const scheme = SchemeServicingManager.getActiveScheme()
        let newRoute

        Logger.log(`getPrevious for ${ currentRoute }`, LOGGING_CATEGORY)

        switch (currentRoute) {
            case 'PlanSummaryReview':
            // PlanSummaryReview -> BasketSummary only if the scheme has services
                newRoute = scheme.services && scheme.services.length > 0
                    ? { name: 'BasketSummary' }
                    : { name: 'PlanNotesForm' }
                break
            case 'BasketSummary':
                newRoute = fulfillsServiceRequirements(scheme, SERVICE_OFFERINGS.financeOptions)
                    ? { name: 'FinancialServices' }
                    : fulfillsServiceRequirements(scheme, SERVICE_OFFERINGS.homeEnergyPlan) ? { name: 'HomeEnergyPlan' } : { name: 'PlanServices' }
                break
            case 'FinancialServices':
            // if scheme has the home-energy-plan service available, and
            // the user has chosen the home-energy-plan
                newRoute = fulfillsServiceRequirements(scheme, SERVICE_OFFERINGS.homeEnergyPlan)
                    ? { name: 'HomeEnergyPlan' }
                    : { name: 'PlanServices' }
                break
            case 'HomeEnergyPlan':
                newRoute = { name: 'PlanServices' }
                break
            case 'PlanServices':
                newRoute = this.hasVisitedSummary()
                    ? { name: 'PlanSummaryReview' }
                    : { name: 'PlanNotesForm' }
                break
            case 'PlanNotesForm':
                newRoute = this.hasVisitedSummary()
                    ? { name: 'PlanSummaryReview' }
                    : { name: 'PlanBuilder' }
                break
            case 'PlanBuilder':
                newRoute = Store.getters.lastBackIncludedRoute.name === 'PlanChooser'
                    ? { name: 'PlanChooser' }
                    : this.hasVisitedSummary()
                        ? { name: 'PlanSummaryReview' }
                        : scheme.hasFixedBudget && scheme.hasFixedMotivation
                            ? { name: 'MyHome', query: { id: Store.state.property.id } }
                            : { name: 'PlanChooser' }
                break
            case 'PlanChooser':
                newRoute = this.hasVisitedSummary()
                    ? { name: 'PlanSummaryReview' }
                    : { name: 'MyHome', query: { id: Store.state.property.id } }
                break
            case 'MyHomeEdit':
                newRoute = { name: 'MyHome', query: { id: Store.state.property.id } }
                break
            case 'MyHome':
            case 'AddressChooser':
                newRoute = { name: 'Search' }
                break
            case 'Search':
            default:
                newRoute = { name: 'Welcome' }
        }

        Logger.logObject(newRoute, 'Returning new route', LOGGING_CATEGORY)

        return newRoute
    },
}

export default ProgressManager
